/**
 * ObjectivesChecklist Component
 *
 * Coverage self-report: participant ticks which of the topic's learning
 * objectives were covered. Used at two timepoints:
 * - R3 (RoleInstructions, after learning): Alice marks what her study covered
 * - R6 (PostChatReflection, after the chat): Teacher marks what they covered,
 *   Student marks what their partner covered
 *
 * Objectives are the numbered "(1) Title: desc (2) Title: desc ..." points
 * embedded in config.instructionExplainer (Alice) / config.instructionLearner
 * (Bob). parseObjectives() extracts a {title, full} per point; the checklist
 * shows the titles.
 */

import React from "react";

/**
 * Parse a "(1) Title: desc (2) Title: desc ..." (or "1. ... 2. ...") instruction
 * string into an array of { title, full } objectives. Title = text before the
 * first colon; falls back to the full point when no colon is present.
 */
export function parseObjectives(instruction) {
  if (!instruction || typeof instruction !== "string") return [];

  const points = [];
  const collect = (matches) => {
    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].index + matches[i][0].length;
      const end = i + 1 < matches.length ? matches[i + 1].index : instruction.length;
      const content = instruction.slice(start, end).trim();
      if (content) points.push(content);
    }
  };

  const paren = [...instruction.matchAll(/\((\d+)\)\s*/g)];
  if (paren.length > 0) {
    collect(paren);
  } else {
    const dot = [...instruction.matchAll(/(\d+)\.\s*/g)];
    if (dot.length > 0) collect(dot);
  }

  return points.map((full) => {
    const colon = full.indexOf(":");
    const title = colon > -1 ? full.slice(0, colon).trim() : full;
    return { title, full };
  });
}

/**
 * Build the record saved for a coverage response.
 * @param {Array} objectives - parsed objectives (for titles + total)
 * @param {Array<number>} covered - checked indices
 */
export function buildCoverageRecord(objectives, covered) {
  const sorted = [...covered].sort((a, b) => a - b);
  return {
    covered: sorted,
    coveredTitles: sorted.map((i) => objectives[i]?.title).filter(Boolean),
    count: sorted.length,
    total: objectives.length,
  };
}

const COLORS = {
  blue: { box: "bg-blue-600 border-blue-600", text: "text-blue-900" },
  emerald: { box: "bg-emerald-600 border-emerald-600", text: "text-emerald-900" },
  violet: { box: "bg-violet-600 border-violet-600", text: "text-violet-900" },
};

/**
 * Controlled checklist.
 * @param objectives - array of { title, full }
 * @param covered - array of checked indices
 * @param onToggle - (idx) => void, flips one objective
 */
export function ObjectivesChecklist({
  objectives = [],
  covered = [],
  onToggle,
  color = "blue",
  instructions,
}) {
  const c = COLORS[color] || COLORS.blue;

  if (objectives.length === 0) return null;

  return (
    <div className="space-y-2">
      {instructions && <p className="text-sm text-gray-600 mb-2">{instructions}</p>}
      <ul className="space-y-2">
        {objectives.map((o, idx) => {
          const checked = covered.includes(idx);
          return (
            <li key={idx}>
              <button
                type="button"
                onClick={() => onToggle(idx)}
                aria-pressed={checked}
                className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                  checked ? "bg-gray-50 border-gray-300" : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center ${
                    checked ? c.box : "border-gray-300"
                  }`}
                >
                  {checked && (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <span className={`text-sm font-medium ${checked ? c.text : "text-gray-700"}`}>
                  {o.title}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
